/**
 * Soft map Live thumbnails · plant / device cameras on map.
 * Soft≠Live: STANDBY 备画面 until an authoritative preview ticket URL exists.
 * HARD: 0×0 center host + subpixelPositioning — never sticky offset on Marker.
 */
import { html } from "./ui.js";
import { opsMapCameraBusy } from "./ops-map-device-pins.js";
import { cinemaCardLiveOf, cinemaSessionOpenOf } from "./edgeos-live.js";
import { pullLiveViewAus } from "./udock-api.js";
import {
  AU_EMPTY_FLOOR_MS,
  AU_LAGGY_QUEUE,
  AU_TS_STEP_US,
  AU_WAIT_MS_MINI,
  annexBIfNeeded,
  b64ToU8,
  shouldDropDelta,
  startSingleFlightAuLoop,
} from "./live-au-pump.js";

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function stopPlayer(el) {
  try {
    const pc = el && el.__pc;
    if (pc) pc.close();
  } catch { /* */ }
  if (el) el.__pc = null;
  try {
    if (el && typeof el.__auStop === "function") el.__auStop();
  } catch { /* */ }
  if (el) el.__auStop = null;
  try {
    if (el && el.__auGap) clearTimeout(el.__auGap);
  } catch { /* */ }
  if (el) el.__auGap = null;
  try {
    if (el && el.__auTimer) clearInterval(el.__auTimer);
  } catch { /* */ }
  if (el) el.__auTimer = null;
  try {
    const dec = el && el.__auDecoder;
    if (dec) dec.close();
  } catch { /* */ }
  if (el) el.__auDecoder = null;
  try {
    const video = el && el.querySelector("video");
    if (video) video.srcObject = null;
  } catch { /* */ }
  try {
    const canvas = el && el.querySelector("canvas");
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.clearRect(0, 0, canvas.width || 1, canvas.height || 1);
    }
  } catch { /* */ }
}

/** Soft≠ stretch: pin shell AR to source frame Soft≠ invent Soft crop. */
function applyLiveAspect(el, width, height) {
  if (!el) return;
  const w = Number(width);
  const h = Number(height);
  if (!(w > 0) || !(h > 0)) return;
  const ar = `${Math.round(w)} / ${Math.round(h)}`;
  try {
    el.style.setProperty("--ops-live-ar", ar);
    const screen = el.querySelector(".ops-live-thumb__screen");
    if (screen) screen.style.setProperty("--ops-live-ar", ar);
  } catch { /* */ }
}

function startWhep(el, url) {
  if (!el || !url || typeof RTCPeerConnection === "undefined") return;
  stopPlayer(el);
  const video = el.querySelector("video");
  if (!video) return;
  const onMeta = () => applyLiveAspect(el, video.videoWidth, video.videoHeight);
  try { video.addEventListener("loadedmetadata", onMeta); } catch { /* */ }
  const pc = new RTCPeerConnection({ iceServers: [] });
  el.__pc = pc;
  pc.addTransceiver("video", { direction: "recvonly" });
  pc.ontrack = (ev) => {
    try {
      video.srcObject = ev.streams[0] || null;
      video.muted = true;
      video.playsInline = true;
      video.play().catch(() => {});
      onMeta();
    } catch { /* */ }
  };
  (async () => {
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/sdp" },
        body: offer.sdp || "",
        credentials: "include",
      });
      if (!res.ok || el.__pc !== pc) return;
      const answer = await res.text();
      if (el.__pc !== pc) return;
      await pc.setRemoteDescription({ type: "answer", sdp: answer });
    } catch { /* fail closed — badge stays honest */ }
  })();
}

/** Product map mini Live Soft≠ Soft Cover: WebCodecs AU when WHEP secondary is dark. */
function startAu(el, deviceId, sessionId) {
  if (!el || !deviceId || !sessionId || typeof VideoDecoder === "undefined") return;
  stopPlayer(el);
  const canvas = el.querySelector("canvas");
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  let after = 0;
  let configured = false;
  let dead = false;
  const decoder = new VideoDecoder({
    output: (frame) => {
      if (dead || el.__auDecoder !== decoder) {
        try { frame.close(); } catch { /* */ }
        return;
      }
      try {
        const w = frame.displayWidth || frame.codedWidth || 160;
        const h = frame.displayHeight || frame.codedHeight || 90;
        if (canvas.width !== w) canvas.width = w;
        if (canvas.height !== h) canvas.height = h;
        applyLiveAspect(el, canvas.width, canvas.height);
        ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
      } catch { /* */ }
      try { frame.close(); } catch { /* */ }
    },
    error: () => { /* fail closed Soft≠ invent Soft pixels */ },
  });
  el.__auDecoder = decoder;
  let needKey = true;
  let tsUs = 0;
  const tick = async () => {
    if (dead || el.__auDecoder !== decoder) return;
    const out = await pullLiveViewAus(deviceId, sessionId, {
      max_n: 16,
      after_seq: after,
      wait_ms: AU_WAIT_MS_MINI,
    });
    if (!out || out.ok !== true || dead || el.__auDecoder !== decoder) return;
    const j = out.data || {};
    const laggy = typeof decoder.decodeQueueSize === "number" && decoder.decodeQueueSize > AU_LAGGY_QUEUE;
    for (const it of j.items || []) {
      after = Math.max(after, Number(it.seq || 0));
      if (!it.nal_b64) continue;
      const isKey = !!it.key;
      if (shouldDropDelta(needKey, isKey, laggy)) continue;
      const payload = annexBIfNeeded(b64ToU8(it.nal_b64));
      try {
        if (!configured && isKey) {
          const codec = String(it.codec || j.codec || "avc1.42C01E");
          const hevc = /hev|hvc|hevc|h265/i.test(codec);
          decoder.configure({
            codec,
            optimizeForLatency: true,
            ...(hevc ? { hevc: { format: "annexb" } } : { avc: { format: "annexb" } }),
          });
          configured = true;
          needKey = false;
        }
        if (!configured) continue;
        if (isKey) needKey = false;
        tsUs += AU_TS_STEP_US;
        decoder.decode(
          new EncodedVideoChunk({
            type: isKey ? "key" : "delta",
            timestamp: tsUs,
            data: payload,
          }),
        );
      } catch {
        needKey = true;
      }
    }
  };
  const stopLoop = startSingleFlightAuLoop({
    tick,
    isDead: () => dead || el.__auDecoder !== decoder,
    waitMs: AU_WAIT_MS_MINI,
    emptyFloorMs: AU_EMPTY_FLOOR_MS,
    onGap: (id) => {
      el.__auGap = id;
    },
  });
  el.__auStop = () => {
    dead = true;
    try { stopLoop(); } catch { /* */ }
  };
}

function paintThumb(el, t) {
  const live = !!(t && t.live);
  const useWhep = !!(live && t.whepUrl);
  const useAu = !!(live && !useWhep && t.sessionId && t.deviceId);
  const nearby = !!(t && t.nearby);
  const origin = !!(t && t.origin) || (live && !nearby);
  const label = escapeHtml(String((t && (t.label || t.id)) || "").slice(0, 14));
  /* Soft≠Live: no media_played / no session → STANDBY Soft≠ invent pixels. */
  const reason = escapeHtml(
    String(
      (t && t.reason) ||
        (useWhep
          ? "Live at the point"
          : useAu
            ? (nearby ? "Nearby live" : "Live at the point")
            : "No live stream yet"),
    ).slice(0, 120),
  );
  const status = live ? (nearby ? "Nearby" : origin ? "At the point" : "Live") : "Standby";
  el.__thumb = t || null;
  el.setAttribute("data-soft", live ? "0" : "1");
  el.setAttribute("data-live", live ? "1" : "0");
  el.setAttribute("data-standby", live ? "0" : "1");
  el.setAttribute("data-origin", origin && live ? "1" : "0");
  el.setAttribute("data-nearby", nearby && live ? "1" : "0");
  el.setAttribute("data-proof", live ? "play" : "0");
  el.setAttribute(
    "data-live-player",
    (t && t.livePlayer) || (useWhep ? "whep" : useAu ? "webcodecs_au" : "not_wired"),
  );
  el.setAttribute(
    "aria-label",
    `${live ? "Live" : "Standby"} ${t && (t.label || t.id)}`,
  );
  el.classList.toggle("is-active", !!(t && t.active));
  el.dataset.zone = (t && t.zoneId) || "";
  el.innerHTML = `
    <button type="button" class="ops-live-thumb__screen" aria-expanded="false">
      ${useWhep ? '<video muted playsinline></video>' : (useAu ? '<canvas aria-hidden="true"></canvas>' : '<i class="ops-live-thumb__standby" aria-hidden="true"></i>')}
      <s>${status}</s>
      <u>${label}</u>
    </button>
    <span class="ops-live-thumb__cap"><i>${live ? "live" : "standby"}</i></span>
    <span class="ops-live-thumb__popover">
      <b>${label}</b>
      <small>${reason}</small>
      <button type="button" class="ops-live-thumb__open" ${live ? "" : "disabled"}>
        ${live ? "Open Live" : "Standby"}
      </button>
    </span>`;
  if (useWhep) startWhep(el, t.whepUrl);
  else if (useAu) startAu(el, t.deviceId, t.sessionId);
  else stopPlayer(el);
}

/**
 * Merge Soft map hosts with camera registry + preview tickets.
 * Soft≠Live: never promote to Live without whep_ticket_url.
 */
export function enrichMapThumbsWithRegistry(thumbs, registryItems, ticketsBySourceId) {
  const items = Array.isArray(registryItems) ? registryItems : [];
  const tickets = ticketsBySourceId && typeof ticketsBySourceId === "object" ? ticketsBySourceId : {};
  const matchItem = (thumbId) => {
    const id = String(thumbId || "").trim();
    if (!id) return null;
    return items.find((item) => {
      const kind = String(item && item.source_kind || "");
      const binding = item && item.binding && typeof item.binding === "object" ? item.binding : {};
      const subject = String(item && item.subject_ref || "");
      const cam = String(binding.camera_id || "");
      const device = String(binding.device_id || "");
      const source = String(item && item.source_id || "");
      if (kind === "plant") return subject === id || cam === id || source === id;
      if (kind === "device") return cam === id || device === id || subject === id || source === id;
      if (kind === "personnel") return subject === id || source === id;
      return false;
    }) || null;
  };
  return (Array.isArray(thumbs) ? thumbs : []).map((t) => {
    const item = matchItem(t && t.id) || matchItem(t && t.sourceHint);
    const envelope = item && item.envelope && typeof item.envelope === "object" ? item.envelope : {};
    const session = item && item.session && typeof item.session === "object" ? item.session : {};
    const binding = item && item.binding && typeof item.binding === "object" ? item.binding : {};
    const sourceId = item ? String(item.source_id || "") : String((t && t.sourceHint) || "");
    const ticket = sourceId ? tickets[sourceId] : null;
    const whepUrl = ticket && ticket.whep_ticket_url ? String(ticket.whep_ticket_url) : "";
    const sourceKind = String((item && item.source_kind) || (t && t.sourceKind) || "").trim();
    const sessionId = String(session.session_id || (ticket && ticket.session_id) || "").trim();
    /* Personnel/robot sessions publish WHEP; they are not μDock device AU streams. */
    const deviceId = sourceKind === "device" || sourceKind === "plant"
      ? String(
        binding.device_id || (sourceKind === "device" && item && item.subject_ref)
          || (t && t.deviceId) || "",
      ).trim()
      : "";
    const liveCapable = cinemaCardLiveOf(item);
    /* Live Pixel Rule: live_preview is a ticket. LIVE only when media_played. */
    const live = liveCapable === true;
    const livePlayer = whepUrl ? "whep" : (live ? "webcodecs_au" : "not_wired");
    return {
      ...t,
      sourceId,
      sourceKind,
      sessionId,
      deviceId,
      groupId: String((t && t.groupId) || (item && item.group_id) || ""),
      soft: !live,
      live,
      whepUrl,
      reason: (ticket && ticket.reason) || envelope.reason || "",
      livePlayer,
      aspect: live ? "live_preview" : "soft_preview",
    };
  });
}

/**
 * Production map hosts from Live camera registry + device pins Soft≠ Soft Cover invent.
 * Places a thumb when device (or plant subject) has map coordinates.
 * Live paint still requires WHEP ticket via enrichMapThumbsWithRegistry.
 * Do not unique On-site frames by device_id — two cameras on one board stay two chips.
 */
export function liveRegistryMapThumbsOf(registryItems, devices, _centre, people = []) {
  const byDevice = new Map();
  for (const d of Array.isArray(devices) ? devices : []) {
    const id = String((d && d.id) || "").trim();
    if (id) byDevice.set(id, d);
  }
  const byPerson = new Map();
  for (const person of Array.isArray(people) ? people : []) {
    const id = String((person && person.id) || "").trim();
    if (id) byPerson.set(id, person);
  }
  const out = [];
  const seen = new Set();
  for (const item of Array.isArray(registryItems) ? registryItems : []) {
    if (!item || typeof item !== "object") continue;
    const kind = String(item.source_kind || "");
    if (kind !== "device" && kind !== "plant" && kind !== "personnel") continue;
    const binding = item.binding && typeof item.binding === "object" ? item.binding : {};
    const deviceId = String(binding.device_id || (kind === "device" ? item.subject_ref : "") || "").trim();
    const personId = String(kind === "personnel" ? item.subject_ref : "").trim();
    const camId = String(binding.camera_id || (kind === "plant" ? item.subject_ref : "") || "").trim();
    const sourceId = String(item.source_id || "").trim();
    const thumbId = sourceId || camId || personId || "";
    if (!thumbId || seen.has(thumbId)) continue;
    const env = item.envelope && typeof item.envelope === "object" ? item.envelope : {};
    const act = String(env.actionability || "");
    /* Prefer Live / observe seats Soft≠ dump entire catalog onto map. */
    if (act !== "live_preview" && act !== "observe_only") continue;
    const d = deviceId ? byDevice.get(deviceId) : null;
    const person = personId ? byPerson.get(personId) : null;
    let ll = null;
    let geoSource = "";
    if (person && Array.isArray(person.ll) && person.ll.length >= 2) {
      ll = person.ll;
      geoSource = "personnel_live";
    } else if (d && Array.isArray(d.ll) && d.ll.length >= 2) {
      ll = d.ll;
      geoSource = "device";
    } else if (Array.isArray(item.ll) && item.ll.length >= 2) {
      ll = item.ll;
      geoSource = "registry";
    } else if (item.longitude != null && item.latitude != null) {
      const lng = Number(item.longitude);
      const lat = Number(item.latitude);
      if (Number.isFinite(lng) && Number.isFinite(lat)) {
        ll = [lng, lat];
        geoSource = "registry_latlng";
      }
    }
    if (!ll) continue;
    seen.add(thumbId);
    out.push({
      id: thumbId,
      ll,
      label: String(item.label || camId || deviceId || thumbId).slice(0, 40),
      zoneId: String(item.zone_id || (d && d.zone) || ""),
      groupId: String(item.group_id || (d && d.group) || ""),
      deviceId,
      subjectId: personId || deviceId || camId,
      sourceKind: kind,
      sourceHint: String(item.source_id || ""),
      sourceId,
      geoSource,
      mobile: kind === "personnel",
      soft: act !== "live_preview",
      livePlayer: "not_wired",
      aspect: act === "live_preview" ? "live_preview" : "soft_preview",
      active: false,
    });
  }
  return out.slice(0, 8);
}

/** Live registry hosts first Soft≠ Soft practice Cover Soft invent production. */
export function mergeMapThumbHosts(liveThumbs, softThumbs) {
  const out = [];
  const seen = new Set();
  for (const t of [...(Array.isArray(liveThumbs) ? liveThumbs : []), ...(Array.isArray(softThumbs) ? softThumbs : [])]) {
    const id = String((t && t.id) || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(t);
  }
  return out.slice(0, 8);
}

/**
 * Overlay AI Team remux seats onto map thumbs. Reuses WHEP/AU — no fake player.
 * Seats without registry thumbs still paint when survey coords exist.
 */
export function applyAiLiveMapSeats(thumbs, seats) {
  const list = Array.isArray(seats) ? seats : [];
  const rows = Array.isArray(thumbs) ? thumbs.slice() : [];
  if (!list.length) return rows;
  const byId = new Map();
  for (const s of list) {
    const sessionId = String((s && s.sessionId) || "").trim();
    const deviceId = String((s && s.deviceId) || "").trim();
    if (!sessionId || !deviceId) continue;
    const cameraId = String((s && (s.cameraId || s.id)) || "").trim();
    const sourceId = String((s && s.sourceId) || "").trim();
    if (cameraId) byId.set(cameraId, s);
    if (sourceId) byId.set(sourceId, s);
    if (s && s.id) byId.set(String(s.id), s);
  }
  const seen = new Set();
  const out = rows.map((t) => {
    const hit = byId.get(String((t && t.id) || ""))
      || byId.get(String((t && t.sourceHint) || ""))
      || byId.get(String((t && t.sourceId) || ""))
      || byId.get(String((t && t.cameraId) || ""));
    if (!hit) return t;
    const sessionId = String(hit.sessionId || "").trim();
    const deviceId = String(hit.deviceId || (t && t.deviceId) || "").trim();
    if (!sessionId || !deviceId) return t;
    seen.add(String((t && t.id) || ""));
    if (hit.cameraId) seen.add(String(hit.cameraId));
    if (hit.sourceId) seen.add(String(hit.sourceId));
    return {
      ...t,
      sessionId,
      deviceId,
      live: true,
      nearby: !!hit.nearby,
      origin: !!hit.origin && !hit.nearby,
      soft: false,
      livePlayer: t && t.whepUrl ? "whep" : "webcodecs_au",
      reason: hit.nearby ? "Nearby live" : "Live at the point",
      aspect: "live_preview",
    };
  });
  for (const s of list) {
    const sessionId = String((s && s.sessionId) || "").trim();
    const deviceId = String((s && s.deviceId) || "").trim();
    const id = String((s && (s.id || s.cameraId || s.sourceId)) || "").trim();
    if (!sessionId || !deviceId || !id || seen.has(id)) continue;
    const ll = Array.isArray(s.ll) && s.ll.length >= 2
      ? s.ll
      : (Number.isFinite(Number(s.lng)) && Number.isFinite(Number(s.lat))
        ? [Number(s.lng), Number(s.lat)]
        : null);
    if (!ll) continue;
    seen.add(id);
    out.push({
      id,
      ll,
      label: String((s && s.label) || id).slice(0, 40),
      deviceId,
      sessionId,
      sourceKind: "device",
      live: true,
      nearby: !!s.nearby,
      origin: !!s.origin && !s.nearby,
      soft: false,
      livePlayer: "webcodecs_au",
      reason: s.nearby ? "Nearby live" : "Live at the point",
      aspect: "live_preview",
      active: false,
    });
  }
  return out.slice(0, 8);
}

/**
 * Registry source_ids eligible for map / Camera Live preview mint Soft≠ Soft invent.
 */
export function livePreviewSourceIdsOf(registryItems, limit = 4) {
  const out = [];
  const seen = new Set();
  const cap = Math.max(1, Math.min(Number(limit) || 4, 8));
  const rows = Array.isArray(registryItems) ? registryItems.slice() : [];
  /* Active dispatched personnel outrank fixed cameras within scarce preview seats. */
  rows.sort((a, b) =>
    Number((b && b.source_kind) === "personnel")
      - Number((a && a.source_kind) === "personnel"),
  );
  for (const item of rows) {
    const env = item && item.envelope && typeof item.envelope === "object" ? item.envelope : {};
    if (env.actionability !== "live_preview") continue;
    const session = item && item.session && typeof item.session === "object" ? item.session : {};
    if (!cinemaSessionOpenOf(session)) continue;
    const sid = String((item && item.source_id) || "").trim();
    if (!sid || seen.has(sid)) continue;
    seen.add(sid);
    out.push(sid);
    if (out.length >= cap) break;
  }
  return out;
}

/**
 * Personnel live lamp map from registry DTO only (no invent).
 * @returns {Record<string, boolean>}
 */
export function personnelLiveLampOf(registryItems) {
  const out = {};
  for (const item of Array.isArray(registryItems) ? registryItems : []) {
    if (!item || item.source_kind !== "personnel") continue;
    const user = String(item.subject_ref || "").trim();
    if (!user) continue;
    out[user] = cinemaCardLiveOf(item);
  }
  return out;
}

/** Smooth mobile media markers without remounting the player or blocking map input. */
function smoothMarkerTo(marker, nextLl, durationMs = 480) {
  if (!marker || !Array.isArray(nextLl) || nextLl.length < 2) return;
  const from = Array.isArray(marker.__ll) ? marker.__ll : nextLl;
  const to = [Number(nextLl[0]), Number(nextLl[1])];
  if (!to.every(Number.isFinite)) return;
  if (marker.__moveRaf && typeof cancelAnimationFrame !== "undefined") {
    cancelAnimationFrame(marker.__moveRaf);
  }
  const dx = to[0] - Number(from[0]);
  const dy = to[1] - Number(from[1]);
  if (
    Math.abs(dx) + Math.abs(dy) < 1e-9
    || typeof requestAnimationFrame === "undefined"
    || typeof performance === "undefined"
  ) {
    marker.setLngLat(to);
    marker.__ll = to;
    return;
  }
  const started = performance.now();
  const step = (now) => {
    const p = Math.min(1, Math.max(0, (now - started) / durationMs));
    const eased = 1 - Math.pow(1 - p, 3);
    marker.setLngLat([Number(from[0]) + dx * eased, Number(from[1]) + dy * eased]);
    if (p < 1) marker.__moveRaf = requestAnimationFrame(step);
    else {
      marker.__moveRaf = null;
      marker.__ll = to;
    }
  };
  marker.__moveRaf = requestAnimationFrame(step);
}

/**
 * @param {maplibregl.Map|null} map
 * @param {Map} pool
 * @param {object[]} thumbs
 * @param {*} maplibregl
 * @param {(id:string, zoneId:string, handoff:object)=>void} onOpen
 * @param {boolean} on
 */
export function syncOpsMapLiveThumbs(map, pool, thumbs, maplibregl, onOpen, on = true) {
  if (!map || !maplibregl || !pool) return;
  const list = on && Array.isArray(thumbs) ? thumbs : [];
  const want = new Set(list.map((t) => t.id));
  const busy = opsMapCameraBusy(map);
  map.__opsLiveThumbPool = pool;
  if (!map.__opsLiveThumbFlushWired) {
    map.__opsLiveThumbFlushWired = true;
    map.on("moveend", () => {
      const activePool = map.__opsLiveThumbPool;
      if (!activePool || opsMapCameraBusy(map)) return;
      for (const marker of activePool.values()) {
        if (!marker.__pendingLl) continue;
        const next = marker.__pendingLl;
        marker.__pendingLl = null;
        if (marker.__mobile) smoothMarkerTo(marker, next);
        else {
          marker.setLngLat(next);
          marker.__ll = Array.isArray(next) ? next.slice() : next;
        }
      }
    });
  }

  for (const [id, marker] of [...pool.entries()]) {
    if (want.has(id)) continue;
    if (busy) continue;
    try {
      const host = marker.getElement();
      const el = host && host.querySelector(".ops-live-thumb");
      stopPlayer(el);
      if (marker.__moveRaf && typeof cancelAnimationFrame !== "undefined") {
        cancelAnimationFrame(marker.__moveRaf);
      }
      if (marker.__stopPlayer) marker.__stopPlayer();
      marker.remove();
    } catch { /* */ }
    pool.delete(id);
  }

  for (const t of list) {
    if (!t || !t.id || !t.ll) continue;
    let marker = pool.get(t.id);
    const liveKey = `${t.live ? "1" : "0"}|${t.whepUrl || ""}|${t.sessionId || ""}|${t.livePlayer || ""}|${t.label || ""}|${t.active ? "1" : "0"}|${t.sourceId || ""}|${t.deviceId || ""}|${t.groupId || ""}`;
    if (!marker) {
      const host = document.createElement("div");
      host.style.width = host.style.height = "0";
      host.style.pointerEvents = "auto";
      host.className = "ops-live-thumb-host";
      const el = document.createElement("div");
      el.className = "ops-live-thumb thumb";
      el.__liveKey = liveKey;
      paintThumb(el, t);
      el.addEventListener("click", (ev) => {
        try {
          ev.preventDefault();
          ev.stopPropagation();
        } catch { /* */ }
        const target = ev.target && ev.target.closest ? ev.target.closest("button") : null;
        const current = el.__thumb || t;
        if (target && target.classList.contains("ops-live-thumb__open")) {
          if (current.live) {
            onOpen && onOpen(current.id, current.zoneId || "", {
              sourceId: current.sourceId || "",
              deviceId: current.deviceId || "",
              groupId: current.groupId || "",
            });
          }
          return;
        }
        if (!target || !target.classList.contains("ops-live-thumb__screen")) return;
        const opening = !el.classList.contains("is-open");
        try {
          for (const other of document.querySelectorAll(".ops-live-thumb.is-open")) {
            if (other !== el) other.classList.remove("is-open");
          }
        } catch { /* */ }
        el.classList.toggle("is-open", opening);
        target.setAttribute("aria-expanded", opening ? "true" : "false");
      });
      host.appendChild(el);
      try {
        marker = new maplibregl.Marker({
          element: host,
          anchor: "center",
          pitchAlignment: "viewport",
          rotationAlignment: "viewport",
          subpixelPositioning: true,
        })
          .setLngLat(t.ll)
          .addTo(map);
        marker.__ll = Array.isArray(t.ll) ? t.ll.slice() : t.ll;
        marker.__mobile = !!t.mobile;
        marker.__stopPlayer = () => stopPlayer(el);
        pool.set(t.id, marker);
      } catch {
        continue;
      }
    } else {
      try {
        marker.__mobile = !!t.mobile;
        if (busy) {
          marker.__pendingLl = Array.isArray(t.ll) ? t.ll.slice() : t.ll;
        } else {
          marker.__pendingLl = null;
          if (t.mobile) smoothMarkerTo(marker, t.ll);
          else {
            if (marker.__moveRaf && typeof cancelAnimationFrame !== "undefined") {
              cancelAnimationFrame(marker.__moveRaf);
            }
            marker.__moveRaf = null;
            marker.setLngLat(t.ll);
            marker.__ll = Array.isArray(t.ll) ? t.ll.slice() : t.ll;
          }
        }
        const host = marker.getElement();
        const el = host && (host.querySelector(".ops-live-thumb") || host);
        if (el && el.__liveKey !== liveKey) {
          el.__liveKey = liveKey;
          paintThumb(el, t);
        } else if (el) {
          el.__thumb = t;
          el.classList.toggle("is-active", !!t.active);
          el.dataset.zone = t.zoneId || "";
        }
      } catch { /* */ }
    }
  }
}

export function SoftLiveThumbCard({ thumb, onOpen }) {
  if (!thumb) return null;
  const live = !!(thumb.live && thumb.whepUrl);
  return html`<button
    type="button"
    className="ops-live-thumb thumb"
    data-soft=${live ? "0" : "1"}
    data-live=${live ? "1" : "0"}
    data-standby=${live ? "0" : "1"}
    data-active=${thumb.active ? "1" : "0"}
    onClick=${() => onOpen && onOpen(thumb.id, thumb.zoneId || "")}>
    <span className="ops-live-thumb__screen" aria-hidden="true">
      <s>${live ? "LIVE" : "STANDBY"}</s>
      <u>${thumb.label || thumb.id}</u>
    </span>
    <span className="ops-live-thumb__cap"><i>${live ? "live" : "standby"}</i></span>
  </button>`;
}
